import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Dialog } from "@headlessui/react";
import { PencilIcon, TrashIcon, PlusIcon } from "lucide-react";
import Loading from "../components/Loading";
import { FileBox } from "lucide-react";
import api from "../services/api";

const Product = ({ role }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [, setProfileManagers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    description: "",
    group_id: "",
    profile_manager_id: 2,
    is_active: 1,
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 50;
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
    fetchGroups();
    fetchProfileManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSearch(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/products`, {
        headers: { Authorization: token },
      });
      setProducts(res.data.data);
      setFilteredProducts(res.data.data);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch products", "error");
    }
    setLoading(false);
  };

  const fetchGroups = async () => {
    try {
      const res = await api.get(`/products/group`, {
        headers: { Authorization: token },
      });
      setGroups(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchProfileManagers = async () => {
    try {
      const res = await api.get(`/profile-manager`, {
        headers: { Authorization: token },
      });
      setProfileManagers(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      price: "",
      description: "",
      group_id: "",
      profile_manager_id: "",
      is_active: 1,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: product.price,
      description: product.description,
      group_id: product.group_id,
      profile_manager_id: product.profile_manager_id,
      is_active: product.is_active,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price || !formData.group_id) {
      Swal.fire("Oops!", "Semua field wajib diisi!", "warning");
      return;
    }

    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, formData, {
          headers: { Authorization: token },
        });
        Swal.fire("Berhasil!", "Produk berhasil diupdate!", "success");
      } else {
        await api.post(`/products`, formData, {
          headers: { Authorization: token },
        });
        Swal.fire("Berhasil!", "Produk berhasil ditambahkan!", "success");
      }
      fetchData();
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Terjadi kesalahan", "error");
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Apakah anda yakin?",
      text: "Produk akan dihapus secara permanen!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus!",
      cancelButtonText: "Batal",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/products/${id}`, {
            headers: { Authorization: token },
          });
          Swal.fire("Terhapus!", "Produk berhasil dihapus.", "success");
          fetchData();
        } catch (error) {
          console.error(error);
          Swal.fire("Error", "Gagal menghapus produk", "error");
        }
      }
    });
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const filtered = products.filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    const sorted = [...filteredProducts].sort((a, b) => {
      if (a[field] < b[field]) return order === "asc" ? -1 : 1;
      if (a[field] > b[field]) return order === "asc" ? 1 : -1;
      return 0;
    });
    setSortField(field);
    setSortOrder(order);
    setFilteredProducts(sorted);
  };

  const indexOfLast = currentPage * productsPerPage;
  const indexOfFirst = indexOfLast - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileBox /> Products
        </h1>
      </div>

      <div className="flex justify-between mb-4">
        <input
          type="text"
          placeholder="Cari nama produk..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 w-1/3"
        />
        {role === "billing" && (
          <button
            onClick={openAddModal}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <PlusIcon size={16} /> Tambah Produk
          </button>
        )}
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div className="overflow-x-auto rounded-2xl shadow mt-6">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                <th
                  className="px-6 py-3 text-left font-semibold cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  Nama Produk{" "}
                  {sortField === "name" && (sortOrder === "asc" ? "⬆️" : "⬇️")}
                </th>
                <th
                  className="px-6 py-3 text-left font-semibold cursor-pointer"
                  onClick={() => handleSort("price")}
                >
                  Harga{" "}
                  {sortField === "price" && (sortOrder === "asc" ? "⬆️" : "⬇️")}
                </th>
                <th className="px-6 py-3 text-left font-semibold">Grup</th>
                <th className="px-6 py-3 text-left font-semibold">
                  Profile Manager
                </th>
                <th className="px-6 py-3 text-center font-semibold">Status</th>
                {role === "billing" && (
                  <th className="px-6 py-3 text-center font-semibold">Aksi</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800">
              {currentProducts.map((product) => (
                <tr
                  key={product.id}
                  className="border-b hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="px-6 py-4">{product.name}</td>
                  <td className="px-6 py-4">
                    Rp {parseInt(product.price).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">{product.group_name}</td>
                  <td className="px-6 py-4">
                    {product.profile_manager_description}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-2 py-1 text-white text-xs rounded ${
                        product.is_active === 1 ? "bg-green-500" : "bg-red-500"
                      }`}
                    >
                      {product.is_active === 1 ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  {role === "billing" && (
                    <td className="px-6 py-4 flex justify-center gap-3">
                      <button
                        onClick={() => openEditModal(product)}
                        className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:text-blue-800 rounded-md border border-blue-600 hover:border-blue-800 text-sm transition"
                      >
                        <PencilIcon size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="flex items-center gap-1 px-3 py-2 text-red-600 hover:text-red-800 rounded-md border border-red-600 hover:border-red-800 text-sm transition"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex justify-center mt-4 gap-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Panel className="bg-white dark:bg-gray-600 p-6 rounded-lg shadow-lg w-full max-w-md space-y-4">
            <Dialog.Title className="text-lg font-bold text-gray-600 dark:text-white">
              {editingProduct ? "Edit Produk" : "Tambah Produk"}
            </Dialog.Title>

            <input
              type="text"
              placeholder="Nama Produk"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
            />

            <input
              type="number"
              placeholder="Harga"
              value={formData.price}
              onChange={(e) =>
                setFormData({ ...formData, price: e.target.value })
              }
              className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
            />

            <textarea
              placeholder="Deskripsi"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
            />

            <select
              value={formData.group_id}
              onChange={(e) =>
                setFormData({ ...formData, group_id: e.target.value })
              }
              className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="">Pilih Grup</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2">
              <button
                onClick={handleSubmit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl"
              >
                Simpan
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-black dark:text-white px-4 py-2 rounded-xl"
              >
                Batal
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default Product;
